import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getApi, postApi } from "../services/api";
import { toast } from "react-toastify";
import { BASE_UPLOADS } from "../utils/constants";
import { AuthContext } from "../context/AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import { Button, Form, Input, Modal } from "antd";

const CourseDetailPage = () => {
	const { courseId } = useParams();
	const [course, setCourse] = useState(null);
	const [classrooms, setClassrooms] = useState([]);
	const [selectedClassroom, setSelectedClassroom] = useState(""); // State for selected classroom
	const { user } = useContext(AuthContext); // Get user data from auth context
	const [isModalVisible, setModalVisible] = useState(false);
	const [cbeSelected, setCbeSelected] = useState(false);
	const [paymentType, setPaymentType] = useState(null);

	useEffect(() => {
		const fetchCourseDetails = async () => {
			try {
				const response = await getApi(`/courses/${courseId}`);
				if (response && !response.error) {
					setCourse(response);
				} else {
					console.error(
						"Error fetching course details:",
						response.message || "Unknown error"
					);
				}
			} catch (error) {
				console.error("Error fetching course details:", error.message);
			}
		};

		fetchCourseDetails();
	}, [courseId]);

	console.log(course);

	// Fetch classrooms data
	useEffect(() => {
		const fetchClassrooms = async () => {
			try {
				const response = await getApi(`/classrooms`, {
					filter: JSON.stringify({ courseId }),
				});
				if (response && !response.error) {
					setClassrooms(response);
				} else {
					console.error(
						"Error fetching classrooms:",
						response.message || "Unknown error"
					);
					toast.error(
						response.message || "Failed to fetch classrooms."
					);
				}
			} catch (error) {
				console.error("Error fetching classrooms:", error.message);
				toast.error("Error fetching classrooms. Please try again.");
			}
		};

		fetchClassrooms();
	}, [courseId]);

	if (!course) return <p>Loading...</p>;

	const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

	// Handle Enroll Button Click
	const handleEnroll = async (values) => {
		if (paymentType === "cbe" && cbeSelected) {
			const response = await postApi("/payment/cbe", {
				classroomId: selectedClassroom,
				userId: user._id,
				amount: course.price,
				status: "incomplete",
				transactionNo: values?.transactionNo,
			});
			if (response.error) {
				toast.error("There was an error:", response.message);
			}
			setCbeSelected(false);
			setModalVisible(false);
			toast.success("Payment submitted successfully");
			return;
		}
		try {
			const payload = {
				courseName: course.name,
				coursePrice: course.price,
				userId: user._id,
				courseId: course._id,
				classroomId: selectedClassroom,
			};

			const response = await postApi(
				"/stripe/create-checkout-session",
				payload
			);

			if (response && response.sessionId) {
				const stripe = await stripePromise;
				const { error } = await stripe.redirectToCheckout({
					sessionId: response.sessionId,
				});

				if (error) {
					console.error("Stripe Checkout Error:", error.message);
					toast.error(
						"An error occurred while redirecting to payment."
					);
				}
			} else {
				toast.error(
					response?.error || "Failed to initiate payment session."
				);
			}
		} catch (error) {
			console.error("Enrollment Error:", error.message);
			toast.error("Error enrolling into course. Please try again.");
		}
	};

	return (
		<div className="min-h-screen p-8 bg-gray-100 text-gray-900">
			<div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden">
				{/* Course Image */}
				<div>
					<img
						src={
							course.image
								? `${BASE_UPLOADS}/course-pic/${course.image}`
								: "./images/coding.jpg"
						}
						alt={course.name}
						className="w-full h-90 object-cover"
					/>
				</div>

				{/* Course Details */}
				<div className="p-8">
					{/* Course Title */}
					<h1 className="text-4xl font-bold text-blue-700 mb-4 text-center">
						{course.name}
					</h1>

					{/* Course Description */}
					<p className="text-lg text-gray-700 mb-6">
						{course.description}
					</p>

					{/* Course Category and Price */}
					<div className="flex justify-between items-center mb-6">
						<div>
							<h2 className="text-xl font-semibold text-gray-800">
								Category
							</h2>
							<p className="text-gray-600">
								{course?.category?.title || "N/A"}
							</p>
						</div>
						<div>
							<h2 className="text-xl font-semibold text-gray-800">
								Price
							</h2>
							<p className="text-green-600 font-bold">
								${course.price}
							</p>
						</div>
					</div>

					{/* Teachers Section */}
					<div className="mb-6">
						<h2 className="text-lg font-medium mb-4">
							Instructors
						</h2>
						{course.teachers && course.teachers.length > 0 ? (
							<div className="flex flex-wrap items-center gap-6">
								{course.teachers.map((teacher) => (
									<div
										key={teacher._id}
										className="flex items-center space-x-4 bg-indigo-50 p-4 rounded-lg shadow-md"
									>
										<img
											src={
												teacher.profilePic
													? `${BASE_UPLOADS}/profile/${teacher._id}/${teacher.profilePic}`
													: "./images/default-profile.jpg"
											}
											alt={teacher.fullName}
											className="w-16 h-16 rounded-full border-2 border-blue-600"
										/>
										<span className="text-xl font-semibold text-gray-800">
											{teacher.fullName ||
												"Unnamed Instructor"}
										</span>
									</div>
								))}
							</div>
						) : (
							<p className="text-gray-600">
								No instructors available.
							</p>
						)}
					</div>

					{/* Schedule */}
					<div className="flex justify-between items-center mb-6">
						<div className="mb-6">
							<h2 className="text-xl font-semibold text-gray-800 mb-2">
								Schedule
							</h2>
							<ul className="list-disc pl-6 space-y-1 text-gray-700">
								{course.schedule.map((time, index) => (
									<li key={index}>{time}</li>
								))}
							</ul>
						</div>
						<div className="mb-6">
							<h2 className="text-xl font-semibold text-gray-800 mb-2">
								Session Time
							</h2>
							<ul className="list-disc pl-6 space-y-1 text-gray-700">
								{course.sessionTimes.map((time, index) => (
									<li key={index}>{time}</li>
								))}
							</ul>
						</div>
						<div className="mb-6">
							<h2 className="text-xl font-semibold text-gray-800 mb-2">
								Max Students/Class
							</h2>
							<p>{course.maxStudents}</p>
						</div>
					</div>

					{/* Duration */}
					<div className="mb-6">
						<h2 className="text-xl font-semibold text-gray-800 mb-2">
							Duration
						</h2>
						<p className="text-gray-700">
							{course.durationInMinutes} minutes
						</p>
					</div>

					{/* Prerequisites */}
					<div className="mb-6">
						<h2 className="text-xl font-semibold text-gray-800 mb-2">
							Prerequisites
						</h2>
						<p className="text-gray-700">
							{course?.prerequisiteCourses?.length
								? course.prerequisiteCourses.map(
										(prerequisite, index) => (
											<span key={index}>
												{prerequisite.name}
												{index <
													course.prerequisiteCourses
														.length -
														1 && (
													<span className="mx-2">
														•
													</span>
												)}
											</span>
										)
								  )
								: "None"}
						</p>
					</div>

					{/* Classrooms Section */}
					<div className="mb-8">
						<h2 className="text-xl font-semibold text-gray-800 mb-4">
							Select a Classroom
						</h2>
						{classrooms.length > 0 ? (
							<div>
								<select
									value={selectedClassroom}
									onChange={(e) =>
										setSelectedClassroom(e.target.value)
									}
									className="w-full p-3 border rounded-lg"
								>
									<option value="" disabled>
										-- Select a Classroom --
									</option>
									{classrooms.map((classroom) => (
										<option
											key={classroom._id}
											value={classroom._id}
										>
											{classroom.name} -{" "}
											{classroom.userId?.fullName ||
												"Unknown Instructor"}
										</option>
									))}
								</select>
							</div>
						) : (
							<p className="text-gray-600">
								No classrooms available for this course.
							</p>
						)}
					</div>
					<Modal
						open={isModalVisible}
						onCancel={() => setModalVisible(false)}
						onOk={() => {
							if (paymentType === "cbe") {
								setCbeSelected(true);
								return;
							} else if (paymentType === "stripe") {
								handleEnroll();
							} else {
								toast.error(
									"You must select a payment gateway"
								);
							}
						}}
					>
						<div className="flex gap-8 p-5 m-5">
							<div
								className={`rounded-xl border-2 ${
									paymentType === "stripe"
										? " border-blue-400 bg-gray-50 "
										: "border-gray-300"
								} p-4 hover:border-blue-400 `}
								onClick={() => setPaymentType("stripe")}
							>
								<img src="/images/stripe.png" alt="Stripe" />
							</div>
							<div
								className={`rounded-xl border-2 ${
									paymentType === "cbe"
										? "  border-blue-400 bg-gray-50"
										: "border-gray-300"
								} p-4 hover:border-blue-400 `}
								onClick={() => setPaymentType("cbe")}
							>
								<img src="/images/CBE.svg" alt="CBE" />
							</div>
						</div>
					</Modal>
					<Modal
						open={cbeSelected}
						onCancel={() => setCbeSelected(false)}
						width={800}
						footer={null}
					>
						<div className="grid grid-cols-2 gap-8 w-full h-full justify-center items-center p-4 m-1">
							<div className="relative top-5 p-6">
								<img src="/images/CBE.svg" alt="CBE logo" />
							</div>
							<Form
								layout="vertical"
								className="w-full"
								size="large"
								onFinish={handleEnroll}
							>
								<Form.Item
									label="Transaction Number"
									name={"transactionNo"}
								>
									<Input placeholder="xxx-xxxx-xxxx" />
								</Form.Item>
								<Form.Item>
									<Button type="primary" htmlType="submit">
										Submit
									</Button>
								</Form.Item>
							</Form>
						</div>
					</Modal>

					{/* Enroll Button */}
					<div className="flex justify-center">
						<button
							onClick={() => {
								if (!selectedClassroom) {
									toast.error(
										"Please select a classroom before enrolling."
									);
									return;
								}
								setModalVisible(true);
							}}
							className="bg-blue-600 text-white text-lg py-3 px-6 rounded-lg hover:bg-blue-700 transition-all duration-300 "
						>
							Enroll Now
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CourseDetailPage;
